// @flow

import * as React from 'react';
import { Text, View, StyleSheet } from 'react-native';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import {
    createStore, combineReducers, applyMiddleware, compose,
} from 'redux';
import logger from 'redux-logger';
import thunk from 'redux-thunk';
import { ThemeProvider } from 'react-native-elements';
import { NavigationContainer } from '@react-navigation/native';
import {
    APIProvider, SchemaForm, reducers as speedReducers, ConfirmActions, WSContext,
} from 'speed-js-react';
import {
    ApiClient, ApiClientAuthBackend, AuthLocalStorage, JWTBackend, WebSocketClient,
} from 'speed-js-core';
import { removeUser, setPermissions, setUser } from 'speed-js-react/src/redux/auth/actions';
import { RNE_DEFAULT_FIELDS, RNE_FALLBACK_FIELD_TYPE } from 'speed-js-rn';
import RootStack from 'src/navigation/RootStack';
import { resources } from 'src/api';
import { locale, messages } from 'src/locale';
import theme from 'src/styles/theme';
import 'src/styles/icons';
import ConfirmationActionsRenderer from './components/dialogs/ConfirmationActionsRenderer';
import {
    AUTO_LOGIN_COMPLETE_SCREEN,
    CREATE_DEVICE_SCREEN,
    CREATE_PROFILE_SCREEN, EDIT_DEVICE_SCREEN,
    EDIT_PROFILE_SCREEN,
    HOME_SCREEN,
    LOGIN_SCREEN,
    TRIAL_PERIOD_ACTIVATE, TRIAL_PERIOD_START, TRIAL_PERIOD_QUEUE,
} from './navigation/constants';
import AbsoluteActivityIndicator from './components/AbsoluteActivityIndicator';

// REGISTER FORM FIELDS
SchemaForm.registerFieldTypes(RNE_DEFAULT_FIELDS, RNE_FALLBACK_FIELD_TYPE);

let store = null;

// INIT REDUX
let composeEnhancers = compose;
const middlewares = [thunk];

if (process.env.NODE_ENV !== 'production') {
    if (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) {
        composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;
    }
    middlewares.push(logger);
}

store = createStore(
    combineReducers({
        ...speedReducers,
    }),
    composeEnhancers(applyMiddleware(...middlewares)),
);

// INIT API CLIENT
const apiClient = new ApiClient(
    process.env.REACT_APP_API_ROOT, resources, JWTBackend, {
        AuthStorageClass: AuthLocalStorage,
        authResourceName: 'auth',
    }, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Accept-Language': locale,
    }, (client) => {
        // Set the initial user
        if (client.authorized) {
            client.resources.auth.get().then((response) => {
                const dispatchUserActions = () => {
                    if (!store) {
                        setTimeout(dispatchUserActions, 100);
                    } else {
                        store.dispatch(setUser(response));
                        store.dispatch(setPermissions(response));
                    }
                };
                dispatchUserActions();
            }).catch((response) => {
                if (response.status === 401) {
                    client.authorized = false;
                }
            });
        } else {
            store.dispatch(removeUser());
        }
    },
    'omit',
);

// INIT WEBSOCKET CLIENT
const wsAuth = new ApiClientAuthBackend(apiClient);
const wsClient = new WebSocketClient(process.env.REACT_APP_WS_ENDPOINT, wsAuth, window.sessionStorage);

let prefix = `${process.env.PUBLIC_URL}/`.replace('//', '/');
if (prefix.startsWith('/')) {
    prefix = prefix.substr(1);
}

const config = {
    screens: {
        [HOME_SCREEN]: `${prefix}home`,
        [LOGIN_SCREEN]: `${prefix}login`,
        [CREATE_PROFILE_SCREEN]: `${prefix}profile/add`,
        [CREATE_DEVICE_SCREEN]: `${prefix}device/add`,
        [EDIT_DEVICE_SCREEN]: `${prefix}device/edit/:uuid`,
        [EDIT_PROFILE_SCREEN]: `${prefix}profile/edit/:uuid`,
        [AUTO_LOGIN_COMPLETE_SCREEN]: `${prefix}autologin/complete`,
        [TRIAL_PERIOD_START]: `${prefix}trial/start`,
        [TRIAL_PERIOD_QUEUE]: `${prefix}trial/queue`,
        [TRIAL_PERIOD_ACTIVATE]: `${prefix}trial/activate/:token`,
    },
};

const linking = {
    config,
};

const styles = StyleSheet.create({
    navContainer: {
        height: '100vh',
    },
});

function App(): React.Node {
    return (
        <ThemeProvider theme={theme}>
            <Provider store={store}>
                <APIProvider apiClient={apiClient}>
                    <WSContext.Provider value={wsClient}>
                        <IntlProvider locale={locale} textComponent={Text} messages={messages[locale]}>
                            <View style={styles.navContainer}>
                                <NavigationContainer
                                    linking={linking}
                                    fallback={<AbsoluteActivityIndicator />}
                                >
                                    <ConfirmActions renderer={ConfirmationActionsRenderer}>
                                        <RootStack />
                                    </ConfirmActions>
                                </NavigationContainer>
                            </View>
                        </IntlProvider>
                    </WSContext.Provider>
                </APIProvider>
            </Provider>
        </ThemeProvider>
    );
}

export default App;
